import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ConnectorCard from '../components/ConnectorCard';
import { 
  Search, 
  Filter, 
  X, 
  Loader2, 
  Zap, 
  Building2, 
  MousePointerClick, 
  FlaskConical,
  Sparkles,
  ArrowRight,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Popular connector logos mapping
const popularConnectorLogos = {
  'Stripe': 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
  'Slack': 'https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg',
  'GitHub': 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
  'Shopify': 'https://cdn.shopify.com/shopifycloud/brochure/assets/brand-assets/shopify-logo-primary-logo-456baa801ee66a0a435671082365958316831c9960c480451dd0330bcdae304f.svg',
  'Salesforce': 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg',
  'HubSpot': 'https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png',
};

const MarketplacePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [connectors, setConnectors] = useState([]);
  const [popularConnectors, setPopularConnectors] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ total_connectors: 0, total_industries: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const searchQuery = searchParams.get('search') || '';
  const industryFilter = searchParams.get('industry') || '';
  const categoryFilter = searchParams.get('category') || '';
  const sortBy = searchParams.get('sort') || 'deployment_count';

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [industriesRes, categoriesRes, statsRes, popularRes] = await Promise.all([
          axios.get(`${API}/connectors/industries`),
          axios.get(`${API}/connectors/categories`),
          axios.get(`${API}/connectors/stats`),
          axios.get(`${API}/connectors?limit=6&sort_by=deployment_count`),
        ]);
        setIndustries(industriesRes.data.industries || []);
        setCategories(categoriesRes.data.categories || []);
        setStats(statsRes.data);
        setPopularConnectors(popularRes.data || []);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchConnectors = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (searchQuery) params.set('search', searchQuery);
        if (industryFilter) params.set('industry', industryFilter);
        if (categoryFilter) params.set('category', categoryFilter);
        params.set('sort_by', sortBy);
        params.set('limit', '120');

        const response = await axios.get(`${API}/connectors?${params.toString()}`);
        setConnectors(response.data);
      } catch (error) {
        console.error('Error fetching connectors:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConnectors();
  }, [searchQuery, industryFilter, categoryFilter, sortBy]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || industryFilter || categoryFilter;

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        
        {/* Hero Header */}
        <div className="mb-10">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/30 px-3 py-1">
            <Zap className="w-3 h-3 mr-1.5" />
            Production-Ready Integrations
          </Badge>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
            The Integration Marketplace<br />
            <span className="text-gradient">for AI Agents</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            Browse {stats.total_connectors}+ production-ready connectors across {stats.total_industries}+ industries. 
            Deploy to your AI agents in minutes, not weeks.
          </p>
        </div>

        {/* Platform Statistics Bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          <div className="bg-[#0B1120] border border-white/5 rounded-xl p-4 text-center hover:border-primary/30 transition-colors">
            <div className="text-2xl font-heading font-bold text-white">{stats.total_connectors}+</div>
            <div className="text-xs text-slate-500 mt-1">Connectors</div>
          </div>
          <div className="bg-[#0B1120] border border-white/5 rounded-xl p-4 text-center hover:border-primary/30 transition-colors">
            <div className="flex items-center justify-center gap-1">
              <Building2 className="w-5 h-5 text-accent" />
              <span className="text-2xl font-heading font-bold text-white">{stats.total_industries}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">Industries</div>
          </div>
          <div className="bg-[#0B1120] border border-white/5 rounded-xl p-4 text-center hover:border-primary/30 transition-colors">
            <div className="flex items-center justify-center gap-1">
              <MousePointerClick className="w-5 h-5 text-emerald-400" />
              <span className="text-2xl font-heading font-bold text-white">1-Click</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">Deployment</div>
          </div>
          <div className="bg-[#0B1120] border border-white/5 rounded-xl p-4 text-center hover:border-primary/30 transition-colors">
            <div className="flex items-center justify-center gap-1">
              <FlaskConical className="w-5 h-5 text-amber-400" />
              <span className="text-2xl font-heading font-bold text-white">Sandbox</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">Testing</div>
          </div>
          <div className="bg-[#0B1120] border border-white/5 rounded-xl p-4 text-center hover:border-primary/30 transition-colors col-span-2 md:col-span-1">
            <div className="flex items-center justify-center gap-1">
              <Sparkles className="w-5 h-5 text-purple-400" />
              <span className="text-2xl font-heading font-bold text-white">AI</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">Generator</div>
          </div>
        </div>

        {/* Trust Signal */}
        <div className="mb-10 text-center">
          <p className="text-sm text-slate-500">
            Trusted by developers building AI agents for{' '}
            <span className="text-slate-400">Slack</span>,{' '}
            <span className="text-slate-400">Stripe</span>,{' '}
            <span className="text-slate-400">Shopify</span>,{' '}
            <span className="text-slate-400">Salesforce</span>, and more.
          </p>
        </div>

        {/* Popular Connectors Section */}
        {!hasActiveFilters && popularConnectors.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl font-semibold text-white">Most Used Connectors</h2>
              </div>
              <Link 
                to="/marketplace?sort=deployment_count" 
                className="text-sm text-primary hover:text-primary/80 flex items-center gap-1 group"
              >
                View all
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {popularConnectors.slice(0, 6).map((connector) => (
                <Link
                  key={connector.id}
                  to={`/connector/${connector.id}`}
                  className="bg-[#0B1120] border border-white/5 hover:border-primary/30 rounded-xl p-4 flex flex-col items-center text-center group transition-all hover:-translate-y-1"
                  data-testid={`popular-${connector.service_name?.toLowerCase()}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-secondary/50 flex items-center justify-center mb-3 overflow-hidden">
                    {connector.icon_url ? (
                      <img 
                        src={connector.icon_url} 
                        alt={connector.name} 
                        className="w-10 h-10 object-contain"
                      />
                    ) : (
                      <Zap className="w-7 h-7 text-primary" />
                    )}
                  </div>
                  <h3 className="font-medium text-white text-sm group-hover:text-primary transition-colors">
                    {connector.service_name || connector.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">{connector.category}</p>
                  <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                    <span className="text-emerald-400">{connector.deployment_count?.toLocaleString() || 0}</span> deploys
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder="Search connectors like Slack, Stripe, Shopify, or GitHub..."
                value={searchQuery}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="pl-12 h-14 bg-[#0B1120] border-white/10 text-white placeholder:text-slate-500 text-base focus:border-primary/50 focus:ring-primary/20"
                data-testid="marketplace-search-input"
              />
            </div>
            <Button
              variant="outline"
              className="h-14 px-4 border-white/10 text-slate-400 hover:text-white hover:bg-white/5 md:hidden"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>

          {/* Filters Row */}
          <div className={`flex flex-wrap gap-4 ${showFilters ? 'block' : 'hidden md:flex'}`}>
            <Select value={industryFilter || "all"} onValueChange={(v) => updateFilter('industry', v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48 bg-[#0B1120] border-white/10 h-11" data-testid="industry-filter">
                <SelectValue placeholder="All Industries" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1120] border-white/10">
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map((industry) => (
                  <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={categoryFilter || "all"} onValueChange={(v) => updateFilter('category', v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48 bg-[#0B1120] border-white/10 h-11" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1120] border-white/10">
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(v) => updateFilter('sort', v)}>
              <SelectTrigger className="w-full md:w-48 bg-[#0B1120] border-white/10 h-11" data-testid="sort-filter">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1120] border-white/10">
                <SelectItem value="deployment_count">Most Deployed</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="created_at">Newest</SelectItem>
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                className="text-slate-400 hover:text-white h-11"
                onClick={clearFilters}
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-2" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2">
              {searchQuery && (
                <Badge variant="secondary" className="bg-primary/20 text-primary border-primary/30 px-3 py-1">
                  Search: {searchQuery}
                  <button onClick={() => updateFilter('search', '')} className="ml-2 hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {industryFilter && (
                <Badge variant="secondary" className="bg-accent/20 text-accent border-accent/30 px-3 py-1">
                  {industryFilter}
                  <button onClick={() => updateFilter('industry', '')} className="ml-2 hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
              {categoryFilter && (
                <Badge variant="secondary" className="bg-slate-700 text-slate-300 border-slate-600 px-3 py-1">
                  {categoryFilter}
                  <button onClick={() => updateFilter('category', '')} className="ml-2 hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : connectors.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-16 h-16 rounded-full bg-secondary mx-auto mb-6 flex items-center justify-center">
              <Search className="w-8 h-8 text-slate-500" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-white mb-2">No connectors found</h3>
            <p className="text-slate-400 mb-6">Try adjusting your search or filters</p>
            <Button onClick={clearFilters} variant="outline" className="border-slate-700">
              Clear all filters
            </Button>
          </div>
        ) : (
          <>
            {/* All Connectors Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-xl font-semibold text-white">
                  {hasActiveFilters ? 'Search Results' : 'All Connectors'}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Showing {connectors.length} connector{connectors.length !== 1 ? 's' : ''}
                  {industryFilter && ` in ${industryFilter}`}
                  {categoryFilter && ` - ${categoryFilter}`}
                </p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="connectors-grid">
              {connectors.map((connector) => (
                <ConnectorCard key={connector.id} connector={connector} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketplacePage;
